////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Contact route — get in touch, enquiry form, find us            //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { usePageTitle } from '@/hooks/usePageTitle';
import { EnquiryForm } from '@/components/forms/EnquiryForm';
import { getPage, splitSections } from '@/lib/content';
import { Markdown } from '@/components/content/Markdown';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal } from '@/components/content/Reveal';

const ADDRESS = '204 York Street, South Melbourne, Victoria, 3205';

export function Contact () {
  usePageTitle('Contact');

  const page = getPage('contact');
  if (!page) return null;

  const sections = splitSections(page.bodyMd);
  const getInTouch = sections.find((s) => s.heading === 'Get in touch');
  const enquire = sections.find((s) => s.heading === 'Enquire');

  return (
    <div>
      <PageHero title="Contact" />

      <div className="mx-auto max-w-6xl space-y-10 px-6 py-16">
        {getInTouch && (
          <Reveal as="section" id="get-in-touch">
            <GlassTile className="p-8 sm:p-12" aria-labelledby="get-in-touch-heading">
              <h2 id="get-in-touch-heading" className="text-2xl font-semibold text-ink">Get in touch</h2>
              <Markdown source={getInTouch.bodyMd} className="mt-4" />
            </GlassTile>
          </Reveal>
        )}

        <Reveal as="section" id="enquire">
          <GlassTile className="p-8 sm:p-12" aria-labelledby="enquire-heading">
            <h2 id="enquire-heading" className="text-2xl font-semibold text-ink">Enquire</h2>

            {enquire && <Markdown source={enquire.bodyMd} className="mt-4" />}

            {/* Field set ratified as the build default (1.0.6.md §1.1, Mike
                13/08/2026) — tweaks land in the pre-prod team-review round. */}
            <EnquiryForm />
          </GlassTile>
        </Reveal>

        <Reveal as="section" id="find-us">
          <GlassTile className="p-8 sm:p-12" aria-labelledby="find-us-heading">
            <h2 id="find-us-heading" className="text-2xl font-semibold text-ink">Find us</h2>
            <p className="mt-4 text-ink">{ADDRESS}</p>
            {/* The Google Maps embed configuration was not captured from the live site
                (discovery §5) — linking out to Maps rather than fabricating coordinates or an
                API key. */}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block underline"
            >
              View on Google Maps
            </a>
          </GlassTile>
        </Reveal>
      </div>
    </div>
  );
}
